import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { qnaApi } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { QNA_CATEGORY_OPTIONS, type QnaCategory, type QnaEntry } from '../types'

const SEARCH_DEBOUNCE_MS = 300

function highlight(text: string, query: string) {
  if (!query.trim()) return text
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-200 text-inherit rounded-sm">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}

function QnaForm({ initial, onCancel, onSubmit, submitting }: {
  initial?: QnaEntry
  onCancel: () => void
  onSubmit: (question: string, answer: string, category: QnaCategory) => void
  submitting: boolean
}) {
  const [question, setQuestion] = useState(initial?.question ?? '')
  const [answer, setAnswer] = useState(initial?.answer ?? '')
  const [category, setCategory] = useState<QnaCategory>(initial?.category ?? 'SERVICE')
  const valid = question.trim().length > 0 && answer.trim().length > 0

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">분류</label>
        <select value={category} onChange={e => setCategory(e.target.value as QnaCategory)}
          disabled={submitting} className="input-field">
          {QNA_CATEGORY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">질문</label>
        <input value={question} onChange={e => setQuestion(e.target.value)} disabled={submitting} className="input-field" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">답변</label>
        <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={4} disabled={submitting} className="input-field resize-none" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} disabled={submitting} className="btn-secondary disabled:opacity-50">취소</button>
        <button
          onClick={() => valid && onSubmit(question.trim(), answer.trim(), category)}
          disabled={!valid || submitting}
          className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {submitting ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  )
}

export default function QnaPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = !!user?.admin

  const [entries, setEntries] = useState<QnaEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<QnaCategory | 'ALL'>('ALL')
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<Record<number, 'up' | 'down'>>({})
  const hashHandled = useRef(false)

  useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchList = async () => {
    setLoading(true)
    try {
      const data = await qnaApi.list()
      setEntries(data)
    } catch {
      setError('Q&A 목록을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchList() }, [])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    entries.forEach(e => { counts[e.category] = (counts[e.category] ?? 0) + 1 })
    return counts
  }, [entries])

  const filtered = useMemo(() => {
    const q = query.toLowerCase()
    return entries.filter(e => {
      if (activeCategory !== 'ALL' && e.category !== activeCategory) return false
      if (!q) return true
      return e.question.toLowerCase().includes(q) || e.answer.toLowerCase().includes(q)
    })
  }, [entries, query, activeCategory])

  // 검색 중일 때는 일치하는 항목을 자동으로 펼쳐 클릭 한 번을 줄여준다
  useEffect(() => {
    if (query) {
      setExpandedIds(new Set(filtered.map(e => e.id)))
    } else {
      setExpandedIds(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  // 진입 시 URL 해시(#qna-123)가 있으면 해당 항목을 펼치고 스크롤
  useEffect(() => {
    if (hashHandled.current || loading || entries.length === 0) return
    const hash = window.location.hash.replace('#', '')
    const match = hash.match(/^qna-(\d+)$/)
    if (match) {
      const id = Number(match[1])
      setExpandedIds(prev => new Set(prev).add(id))
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
    hashHandled.current = true
  }, [loading, entries])

  const toggle = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const copyLink = async (id: number) => {
    const url = `${window.location.origin}/qna#qna-${id}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 1500)
    } catch {
      // 클립보드 접근이 차단된 환경 — 조용히 무시
    }
  }

  const handleCreate = async (question: string, answer: string, category: QnaCategory) => {
    setSubmitting(true)
    try {
      await qnaApi.create({ question, answer, category })
      setCreating(false)
      fetchList()
    } catch {
      setError('Q&A 등록에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdate = async (id: number, question: string, answer: string, category: QnaCategory) => {
    setSubmitting(true)
    try {
      await qnaApi.update(id, { question, answer, category })
      setEditingId(null)
      fetchList()
    } catch {
      setError('Q&A 수정에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await qnaApi.delete(id)
      fetchList()
    } catch {
      setError('Q&A 삭제에 실패했습니다.')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="h-12 bg-slate-900 flex items-center gap-3 px-4 shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-3 py-1 transition-colors flex items-center gap-1.5"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          대시보드로 돌아가기
        </button>
        <h1 className="text-white text-sm font-semibold ml-1">자주 묻는 질문 (Q&A)</h1>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 space-y-4">
        <div>
          <p className="text-lg font-semibold text-slate-800">자주 묻는 질문</p>
          <p className="text-sm text-slate-500 mt-0.5">궁금한 내용을 검색하거나 분류에서 찾아보세요.</p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm">{error}</div>}

        <div className="relative">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="예: 견적, 데이터 형식, 좌표계"
            className="input-field pl-9"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveCategory('ALL')}
            className={activeCategory === 'ALL'
              ? 'text-xs font-semibold px-3.5 py-1.5 rounded-full bg-primary-600 text-white'
              : 'text-xs px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}
          >
            전체 <span className="opacity-75">{entries.length}</span>
          </button>
          {QNA_CATEGORY_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setActiveCategory(o.value)}
              className={activeCategory === o.value
                ? 'text-xs font-semibold px-3.5 py-1.5 rounded-full bg-primary-600 text-white'
                : 'text-xs px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}
            >
              {o.label} <span className={activeCategory === o.value ? 'opacity-75' : 'text-slate-400'}>{categoryCounts[o.value] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">{filtered.length}개 항목</p>
          {isAdmin && !creating && (
            <button onClick={() => setCreating(true)} className="text-xs text-primary-600 font-semibold hover:underline">
              + 항목 추가
            </button>
          )}
        </div>

        {creating && (
          <QnaForm onCancel={() => setCreating(false)} onSubmit={handleCreate} submitting={submitting} />
        )}

        {loading ? (
          <div className="text-center text-sm text-slate-400 py-10">불러오는 중...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 space-y-2">
            <p className="text-sm text-slate-500">
              {query ? `'${query}'에 대한 검색 결과가 없습니다.` : '등록된 Q&A가 없습니다.'}
            </p>
            {query && <p className="text-xs text-slate-400">다른 검색어를 입력하거나 전체 분류에서 찾아보세요.</p>}
            <button
              onClick={() => navigate('/contact')}
              className="mt-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              1:1 문의하기
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
            {filtered.map(entry => {
              const expanded = expandedIds.has(entry.id)
              return (
                <div key={entry.id} id={`qna-${entry.id}`}>
                  {editingId === entry.id ? (
                    <div className="p-3">
                      <QnaForm
                        initial={entry}
                        onCancel={() => setEditingId(null)}
                        onSubmit={(q, a, c) => handleUpdate(entry.id, q, a, c)}
                        submitting={submitting}
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => toggle(entry.id)}
                        className="w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-slate-50"
                      >
                        <span className="text-[10px] font-bold text-primary-700 bg-primary-50 px-2 py-0.5 rounded shrink-0 mt-0.5">{entry.categoryLabel}</span>
                        <span className="text-sm font-semibold text-slate-800 flex-1">{highlight(entry.question, query)}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          className={`text-slate-400 shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`}>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      {expanded && (
                        <div className="px-4 pb-4 pl-[4.5rem] -mt-1 space-y-3">
                          <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{highlight(entry.answer, query)}</p>

                          <div className="flex items-center gap-3 flex-wrap text-xs">
                            <button onClick={() => navigate('/contact')} className="text-primary-600 font-medium hover:underline">
                              문의하기 →
                            </button>
                            <span className="flex-1" />
                            {!feedback[entry.id] ? (
                              <>
                                <span className="text-slate-400">도움이 되었나요?</span>
                                <button onClick={() => setFeedback(f => ({ ...f, [entry.id]: 'up' }))}
                                  className="px-2 py-1 border border-slate-200 rounded text-slate-500 hover:border-slate-300">👍 예</button>
                                <button onClick={() => setFeedback(f => ({ ...f, [entry.id]: 'down' }))}
                                  className="px-2 py-1 border border-slate-200 rounded text-slate-500 hover:border-slate-300">👎 아니오</button>
                              </>
                            ) : (
                              <span className="text-slate-400">피드백 감사합니다.</span>
                            )}
                            <button onClick={() => copyLink(entry.id)} className="text-slate-400 hover:text-slate-600">
                              {copiedId === entry.id ? '링크 복사됨' : '🔗 링크 복사'}
                            </button>
                          </div>

                          {isAdmin && (
                            <div className="flex gap-3 pt-1 border-t border-slate-100">
                              <button onClick={() => setEditingId(entry.id)} className="text-[11px] text-slate-500 hover:text-primary-600 mt-2">수정</button>
                              <button onClick={() => handleDelete(entry.id)} className="text-[11px] text-slate-500 hover:text-red-500 mt-2">삭제</button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3.5 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-slate-800">찾으시는 답변이 없나요?</p>
            <p className="text-xs text-slate-500 mt-0.5">담당자가 직접 확인하고 회신드립니다.</p>
          </div>
          <button
            onClick={() => navigate('/contact')}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors shrink-0"
          >
            1:1 문의하기
          </button>
        </div>
      </main>
    </div>
  )
}
